import { useEffect, useRef, useState } from "react";

const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

export const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;
export const sttSupported = !!SR;

// Strip emojis and symbols so the voice doesn't read them out; keep . , ? ! for natural pauses.
export const cleanForSpeech = (text) =>
  String(text || "")
    .replace(/[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}\u{FE0F}\u{200D}\u{20E3}]/gu, "")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\s*[—–]\s*/g, ", ")
    .replace(/[*_#`~>|•·"“”()[\]{}<>\\/^=+]/g, " ")
    .replace(/\s*:\s*/g, ", ")
    .replace(/\s*;\s*/g, ", ")
    .replace(/(\s*[.,!?]){2,}/g, (m) => m.trim().slice(-1))
    .replace(/\s{2,}/g, " ")
    .trim();

export const useSpeaker = ({ onFinished } = {}) => {
  const [speakingId, setSpeakingId] = useState(null);
  const tokenRef = useRef(0);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;
  const stop = () => { tokenRef.current += 1; if (ttsSupported) window.speechSynthesis.cancel(); setSpeakingId(null); };
  const speak = (id, text) => {
    if (!ttsSupported) return;
    tokenRef.current += 1;
    const token = tokenRef.current;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(cleanForSpeech(text));
    u.lang = "en-GB";
    const v = window.speechSynthesis.getVoices().find((x) => x.lang === "en-GB");
    if (v) u.voice = v;
    const end = () => {
      if (token !== tokenRef.current) return;
      setSpeakingId(null);
      if (finishedRef.current) finishedRef.current();
    };
    u.onend = end;
    u.onerror = end;
    setSpeakingId(id);
    try {
      window.speechSynthesis.speak(u);
    } catch (e) {
      console.error("[chat] speech failed", e);
      setSpeakingId(null);
      if (finishedRef.current) finishedRef.current();
    }
  };
  useEffect(() => () => { if (ttsSupported) window.speechSynthesis.cancel(); }, []);
  return { speakingId, speak, stop };
};

// Single-phrase dictation that appends to a field.
export const useDictation = (onText) => {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const start = () => {
    if (!SR || listening) return;
    const rec = new SR();
    rec.lang = "en-GB";
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      const t = Array.from(e.results).map((r) => r[0].transcript).join(" ").trim();
      if (t) onText(t);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };
  const stop = () => { if (recRef.current) recRef.current.stop(); };
  useEffect(() => () => { if (recRef.current) recRef.current.abort(); }, []);
  return { listening, start, stop };
};

// Continuous dictation: live transcript via onUpdate, onDone(text) after `pauseMs` of silence.
// onIdle: recognition ended with nothing said; onFail(code): recognition error.
export const useAutoSendDictation = ({ onUpdate, onDone, onIdle, onFail, pauseMs = 3000 }) => {
  const [listening, setListening] = useState(false);
  const listeningRef = useRef(false);
  const recRef = useRef(null);
  const timer = useRef(null);
  const textRef = useRef("");
  const doneRef = useRef(false);
  const cancelledRef = useRef(false);
  const cbRef = useRef({});
  cbRef.current = { onUpdate, onDone, onIdle, onFail };
  const setL = (v) => { listeningRef.current = v; setListening(v); };
  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const finish = () => {
    clear();
    if (doneRef.current) return;
    doneRef.current = true;
    if (recRef.current) recRef.current.stop();
    if (textRef.current.trim()) cbRef.current.onDone(textRef.current.trim());
  };
  const start = (base = "") => {
    if (!SR || listeningRef.current) return;
    const rec = new SR();
    rec.lang = "en-GB";
    rec.interimResults = true;
    rec.continuous = true;
    textRef.current = base;
    doneRef.current = false;
    cancelledRef.current = false;
    let failed = false;
    rec.onresult = (e) => {
      const spoken = Array.from(e.results).map((r) => r[0].transcript).join(" ").replace(/\s+/g, " ").trim();
      textRef.current = base ? `${base} ${spoken}` : spoken;
      cbRef.current.onUpdate(textRef.current);
      clear();
      timer.current = setTimeout(finish, pauseMs);
    };
    rec.onend = () => {
      setL(false);
      if (cancelledRef.current || recRef.current !== rec) return;
      if (textRef.current.trim() !== base.trim()) finish();
      else { clear(); if (!failed && !doneRef.current && cbRef.current.onIdle) cbRef.current.onIdle(); }
    };
    rec.onerror = (e) => {
      clear();
      if (cancelledRef.current) return;
      failed = e && e.error !== "no-speech";
      if (failed && cbRef.current.onFail) cbRef.current.onFail(e.error);
    };
    recRef.current = rec;
    setL(true);
    try {
      rec.start();
    } catch (e) {
      setL(false);
      if (cbRef.current.onFail) cbRef.current.onFail("start-failed");
    }
  };
  const cancel = () => { clear(); doneRef.current = true; cancelledRef.current = true; if (recRef.current) recRef.current.abort(); setL(false); };
  useEffect(() => () => { clear(); if (recRef.current) recRef.current.abort(); }, []);
  return { listening, start, finish, cancel };
};
